import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  DFC_ARABIC_ENABLED,
  DfcDirection,
  DfcLocale,
  getDfcDirection,
  isDfcLocale,
} from '../Utils/dfcLocale';

/**
 * English / Arabic for the DFC Fitness Village theme (`company.theme === 'dfcVillageTheme'`).
 *
 * Mirrors `DfcThemeModeContext`: the header's language switch sets it, the choice is remembered
 * on this device, and with no choice yet every visit starts in English. A `?lang=ar` (or `en`)
 * link wins over the stored choice and becomes it, so DFC's Arabic site can link straight in.
 *
 * Only the village design reads it — copy through `useFitnessVillageCopy()`, dates through
 * `formatDfcDate` — and `App` turns the page right-to-left for Arabic on a village company only.
 */
export const DFC_LOCALE_STORAGE_KEY = 'dfcLocale';

/** The query parameter a link can carry to open the village in a given language. */
export const DFC_LOCALE_QUERY_PARAM = 'lang';

export interface DfcLocaleContextProps {
  locale: DfcLocale;
  direction: DfcDirection;
  setLocale: (locale: DfcLocale) => void;
}

const persistDfcLocale = (locale: DfcLocale) => {
  try {
    window.localStorage.setItem(DFC_LOCALE_STORAGE_KEY, locale);
  } catch {
    // The choice simply won't survive a reload.
  }
};

const readInitialDfcLocale = (): DfcLocale => {
  if (!DFC_ARABIC_ENABLED) return 'en';

  const linked = new URLSearchParams(window.location.search).get(DFC_LOCALE_QUERY_PARAM);
  if (isDfcLocale(linked)) {
    persistDfcLocale(linked);
    return linked;
  }

  try {
    const stored = window.localStorage.getItem(DFC_LOCALE_STORAGE_KEY);
    if (isDfcLocale(stored)) return stored;
  } catch {
    // Storage can be unavailable (private mode, blocked site data) — fall through.
  }
  return 'en';
};

export const DfcLocaleContext = createContext<DfcLocaleContextProps>({
  locale: 'en',
  direction: 'ltr',
  setLocale: () => undefined,
});

export const DfcLocaleProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [locale, setLocaleState] = useState<DfcLocale>(readInitialDfcLocale);

  const setLocale = useCallback((next: DfcLocale) => {
    if (!DFC_ARABIC_ENABLED) return;
    setLocaleState(next);
    persistDfcLocale(next);
  }, []);

  const value = useMemo(
    () => ({ locale, direction: getDfcDirection(locale), setLocale }),
    [locale, setLocale]
  );

  return <DfcLocaleContext.Provider value={value}>{children}</DfcLocaleContext.Provider>;
};

export const useDfcLocale = (): DfcLocaleContextProps => useContext(DfcLocaleContext);
